import { Button, Text } from "@adoptaunabuelo/react-components";
import { ArrowLeft } from "lucide-react";
import styled from "styled-components";
import { ColorV2 } from "@adoptaunabuelo/react-components";
import type { UserScheme } from "client-v2";
import cakeAirImage from "../../../assets/images/felicitacion/CakeAir.webp";
import mobileAirImage from "../../../assets/images/felicitacion/MobileAir.webp";

interface Props {
	selectedGrandpa?: UserScheme;
	onBack: () => void;
	onContinue: () => void;
}

const Page = styled.div`
	display: flex;
	flex-direction: column;
	height: 100dvh;
	min-height: 100vh;
	background: var(--surface-background, #f9f6f3);
	overflow: hidden;
	overflow-x: hidden;
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	background: var(--surface-background, #f9f6f3);
	padding: 8px 24px 20px 0px;
`;

const HeaderRow = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 48px;
`;

const BackButton = styled(Button)`
	position: absolute;
	margin: 0;
	padding: 12px;
	left: 0;
`;

const Content = styled.div`
	display: flex;
	flex: 1;
	min-height: 0;
	background: ${ColorV2.surface.invert};
	border-radius: 24px;
	margin-top: -8px;
	overflow-y: auto;
	padding: 0 24px;
`;

const Card = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 24px 0;
	background: ${ColorV2.surface.invert};
	overflow: hidden;
`;

const Copy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;

	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		gap: 14px;
	}

	@media (max-width: 380px) {
		gap: 12px;
	}
`;

const SecondaryCopy = styled(Copy)`
	width: 100%;
	text-align: left;
`;

const SupportHeading = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const MainTitle = styled(Text)`
	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		font-size: 28px !important;
		line-height: 1.08 !important;
	}

	@media (max-width: 380px) {
		font-size: 24px !important;
		line-height: 1.08 !important;
	}
`;

const IntroText = styled(Text)`
	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		font-size: 14px !important;
		line-height: 1.42 !important;
	}

	@media (max-width: 380px) {
		font-size: 13px !important;
		line-height: 1.4 !important;
	}
`;

const SupportEyebrow = styled(Text)`
	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		font-size: 28px !important;
		line-height: 1.08 !important;
	}

	@media (max-width: 380px) {
		font-size: 24px !important;
		line-height: 1.08 !important;
	}
`;

const SupportTitle = styled(Text)`
	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		font-size: 28px !important;
		line-height: 1.08 !important;
	}

	@media (max-width: 380px) {
		font-size: 24px !important;
		line-height: 1.08 !important;
	}
`;

const SupportBody = styled(Text)`
	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		font-size: 14px !important;
		line-height: 1.42 !important;
	}

	@media (max-width: 380px) {
		font-size: 13px !important;
		line-height: 1.4 !important;
	}
`;

const IllustrationWrap = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 24px 0;
`;

const IllustrationStage = styled.div`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

const Illustration = styled.img`
	display: block;
	width: 160px;
	height: 160px;
	object-fit: cover;
	border-radius: 16px;

	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		width: 146px;
		height: 146px;
	}

	@media (max-width: 380px) {
		width: 132px;
		height: 132px;
		border-radius: 14px;
	}
`;

const FloatingBadge = styled.img<{
	$size?: string;
	$placement: "left-top" | "right-bottom";
}>`
	position: absolute;
	width: ${(props) => props.$size ?? "88px"};
	height: auto;
	pointer-events: none;
	${(props) =>
		props.$placement === "left-top"
			? `
		right: 100%;
		top: 0;
		transform: translate(-1px, 0);
	`
			: `
		left: 100%;
		bottom: 0;
		transform: translate(1px, 0);
	`}

	@media (max-width: 380px) {
		width: 60px;
	}

	@media (min-width: 360px) and (max-width: 380px) and (min-height: 720px) {
		width: 68px;
	}
`;

const Footer = styled.div`
	flex-shrink: 0;
	right: 0;
	bottom: 0;
	left: 0;
	padding: 16px 24px 24px;
	background: var(--surface-background, #f9f6f3);
`;

const FelicitacionFinish = ({ selectedGrandpa, onBack, onContinue }: Props) => {
	return (
		<Page>
			<Header>
				<HeaderRow>
					<BackButton
						design="image"
						icon={<ArrowLeft />}
						onClick={onBack}
					/>
				</HeaderRow>
			</Header>

			<Content>
				<Card>
					<Copy>
						<MainTitle type="h1" weight="semibold">
							¡Eres increíble!
						</MainTitle>

						<IntroText type="h6" weight="regular">
							Gracias por compartir tu tiempo con{" "}
							{selectedGrandpa?.name ?? "esta persona mayor"}. En
							cuanto llegue tu felicitación te enviaremos un video
							con su respuesta.
						</IntroText>
					</Copy>

					<IllustrationWrap>
						<IllustrationStage>
							<FloatingBadge
								alt="Tarta decorativa"
								src={cakeAirImage}
								$size="80px"
								$placement="left-top"
							/>
							<Illustration
								alt={`Retrato de ${selectedGrandpa?.name ?? "la persona mayor"}`}
								src={selectedGrandpa?.image?.url ?? undefined}
							/>
							<FloatingBadge
								alt="Movil decorativo"
								src={mobileAirImage}
								$size="80px"
								$placement="right-bottom"
							/>
						</IllustrationStage>
					</IllustrationWrap>

					<SecondaryCopy>
						<SupportHeading>
							<SupportEyebrow type="h2" weight="regular">
								¡Una cosa más!
							</SupportEyebrow>

							<SupportTitle type="h2" weight="semibold">
								Con tu apoyo, podemos dar voz y acompañar a más personas mayores
							</SupportTitle>
						</SupportHeading>
						<SupportBody type="h6" weight="regular">
							Hazte embajador de Adopta Un Abuelo para que sigamos mejorando la vida de nuestros mayores y construyendo entre todos una sociedad más inclusiva.
						</SupportBody>
					</SecondaryCopy>
				</Card>
			</Content>

			<Footer>
				<Button onClick={onContinue} style={{ width: "100%" }}>
					Quiero ser embajador
				</Button>
			</Footer>
		</Page>
	);
};

export default FelicitacionFinish;
